"use client";

import { LoginModal } from "@/components/auth/LoginModal";
import { usePublicProperty } from "@/hooks/usePublicData";
import { useReserve } from "@/hooks/useReserve";
import { getImageWithFallback } from "@/lib/default-image";
import { reservationsService } from "@/services/reservations.service";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import BookingCard from "./BookingCard";

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

/**
 * Book without opening the unit page: dates + guests + live availability +
 * price, then straight to checkout. Bottom sheet on phones, dialog on desktop.
 */
export default function QuickBook({
  propertyId,
  preview,
  isAr,
  onClose,
  defaultCheckIn = null,
  defaultCheckOut = null,
  defaultGuests = 1,
}: {
  propertyId: string;
  /** what the card already knows (instant header while the unit loads) */
  preview?: { title?: string; image?: string; unitCode?: string };
  isAr: boolean;
  onClose: () => void;
  defaultCheckIn?: Date | null;
  defaultCheckOut?: Date | null;
  defaultGuests?: number;
}) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const { data: property, isLoading } = usePublicProperty(propertyId);
  const p = property as any;
  const id = p?._id || p?.id || "";
  const { data: blocked = [] } = useQuery({
    queryKey: ["blocked-dates", id],
    queryFn: () => reservationsService.getBlockedDates(id),
    enabled: !!id,
    staleTime: 5_000,
  });
  const { reserve, showLogin, setShowLogin } = useReserve();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!mounted) return null;

  const title = p ? (isAr ? p.title?.ar : p.title?.en) || p.title?.ar : preview?.title || "";
  const image = getImageWithFallback(p?.images?.[0] || preview?.image || null, "property");
  const code = p?.unitCode || preview?.unitCode;
  const excludeDates = [...new Set([...(p?.blockedDates || []), ...blocked])]
    .map((s: string) => new Date(`${s}T00:00:00`))
    .filter((d) => !Number.isNaN(d.getTime()));

  return createPortal(
    <div className="najdi fixed inset-0 z-[10010] flex items-end justify-center sm:items-center sm:p-4" dir={isAr ? "rtl" : "ltr"} role="dialog" aria-modal="true" aria-label={t("اختر تواريخ الإقامة", "Choose your dates")}>
      <div className="absolute inset-0 bg-shade/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-surface shadow-2xl sm:max-w-md sm:rounded-3xl">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-clay-100 p-4">
          <div className="relative size-14 shrink-0 overflow-hidden rounded-xl bg-clay-50">
            <Image src={image} alt="" fill unoptimized className="object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold text-ink-900">{title}</p>
            {code && (
              <span dir="ltr" className="font-mono text-[11px] font-semibold text-clay-700">
                {code}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label={t("إغلاق", "Close")} className="flex size-9 items-center justify-center rounded-full hover:bg-clay-50">
            <Icon name="close" className="text-[20px]" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-4">
          {isLoading || !p ? (
            <div className="flex h-72 items-center justify-center">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-clay-100 border-t-clay-600" />
            </div>
          ) : (
            <BookingCard
              embedded
              propertyId={id}
              price={p.pricePerNight}
              nightlyPrices={p.nightlyPrices}
              deal={p}
              maxGuests={p.guests}
              isAr={isAr}
              onReserve={(d) => reserve(id, d)}
              excludeDates={excludeDates}
              defaultCheckIn={defaultCheckIn}
              defaultCheckOut={defaultCheckOut}
              defaultGuests={defaultGuests}
            />
          )}
        </div>
      </div>
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>,
    document.body,
  );
}
